import type { Params } from 'react-router';
import type { ProductDetailsFragment } from '@boc/framework/saleor-graphql';
import {
  createProductBreadcrumbBuilder,
  createAttributeBreadcrumbStrategy,
  createCategoryBreadcrumbStrategy,
  createCustomBreadcrumbStrategy,
  deduplicateConsecutiveBreadcrumbs,
} from '@boc/framework/products/breadcrumb-builder';

import { urls } from '~/urls';

/**
 * JigsawNation specific breadcrumb configuration
 */
export const buildProductBreadcrumbs = createProductBreadcrumbBuilder(
  [
    // Category breadcrumb
    createCategoryBreadcrumbStrategy({
      id: 'category',
      getUrl: ({ routeParams, category }) =>
        urls.products.categories(routeParams, { slug: category?.slug }),
      priority: 30,
    }),

    // Custom strategy for jigsaw puzzles - piece count breadcrumb
    createCustomBreadcrumbStrategy({
      id: 'puzzle-pieces',
      priority: 20,
      condition: (product) => product.productType.slug === 'jigsaw-puzzle',
      generate: ({ product, routeParams }) => {
        const pieceCountAttr = product.attributes.find(
          (attr) => attr.attribute.slug === 'puzzle-piece-count'
        );
        const piecesValueAttr = product.attributes.find(
          (attr) => attr.attribute.slug === 'puzzle-pieces'
        );

        if (
          !pieceCountAttr?.values?.length ||
          !piecesValueAttr?.values?.length ||
          !product.category?.slug
        ) {
          return [];
        }

        const piecesValue = piecesValueAttr.values[0].name || piecesValueAttr.values[0].slug;
        const pieceCountSlug = pieceCountAttr.values[0].slug;

        return [{
          name: `${piecesValue} piece puzzles`,
          url: urls.products.attributeWithCategory(routeParams, {
            categorySlug: product.category.slug,
            attributeSlug: 'puzzle-piece-count',
            valueSlug: pieceCountSlug,
          }),
        }];
      },
    }),
  ],
  {
    homeName: 'Home',
    includeHome: true,
    includeShop: true,
    shopName: 'Shop',
    includeProductName: true,
  }
);

/**
 * Get breadcrumbs for a product page
 */
export function getProductBreadcrumbs(
  product: ProductDetailsFragment,
  routeParams: Params
) {
  const breadcrumbs = buildProductBreadcrumbs({
    product,
    routeParams,
    urls,
  });

  // Remove any duplicate consecutive breadcrumbs
  return deduplicateConsecutiveBreadcrumbs(breadcrumbs);
}

/**
 * Get breadcrumbs for a category page
 */
export function getCategoryBreadcrumbs(
  category: { name: string; slug: string },
  routeParams: Params
) {
  return [
    {
      name: 'Home',
      url: urls.home(routeParams),
    },
    {
      name: 'Shop',
      url: urls.products.index(routeParams),
    },
    {
      name: category.name,
      url: urls.products.categories(routeParams, { slug: category.slug }),
    },
  ];
}

/**
 * Get breadcrumbs for attribute-based pages
 * This is a stub for backwards compatibility - can be implemented if needed
 */
export function getAttributeBreadcrumbs(
  routeParams: Params,
  options: {
    categorySlug?: string;
    categoryName?: string;
    attributeSlug?: string;
    attributeName?: string;
    valueSlug?: string;
    valueName?: string;
  } = {}
) {
  const breadcrumbs = [
    {
      name: 'Home',
      url: urls.home(routeParams),
    },
    {
      name: 'Shop',
      url: urls.products.index(routeParams),
    },
  ];

  if (options.categorySlug && options.categoryName) {
    breadcrumbs.push({
      name: options.categoryName,
      url: urls.products.categories(routeParams, { slug: options.categorySlug }),
    });
  }

  if (options.attributeSlug && options.valueName && options.categorySlug && options.valueSlug) {
    breadcrumbs.push({
      name: options.valueName,
      url: urls.products.attributeWithCategory(routeParams, {
        categorySlug: options.categorySlug,
        attributeSlug: options.attributeSlug,
        valueSlug: options.valueSlug,
      }),
    });
  }

  return breadcrumbs;
}
